import React, { useContext, useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../../contexts/SupabaseAuthContext";
import LanguageSwitcher from "../LanguageSwitcher";

import logo from "../../assets/right-click1.png";
import personLogo from "../../assets/person2.png";
// Import additional logos for menu items
import divorceGuideLogo from "../../assets/engagement-ring.png";
import timelineImg from "../../assets/timeline.png";
import videoLogo from "../../assets/video.png";
import calcLogo from "../../assets/calories-calculator.png";
import checklistLogo from "../../assets/checklistf.png";
import legalAidLogo from "../../assets/handshake.png";
import personLogo2 from "../../assets/relax.png";

const MobileHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');

  return (
    <header
      className="
        flex items-center justify-between
        p-4 lg:hidden
        fixed top-0 left-0 right-0
        bg-primary text-white shadow
        z-[9999]
      "
      role="banner"
    >
      <div className="flex items-center">
        <img src={logo} alt="Logo" className="h-8 w-8 mr-2" />
        <span className="text-xl font-bold">
          clicdroits<span className="text-secondary">.info</span>
        </span>
      </div>
      <div className="flex items-center">
        {/* Language Switcher for mobile */}
        <div className="mr-2">
          <LanguageSwitcher />
        </div>
        
        {location.pathname !== "/dashboard" && (
          <button
            onClick={() => navigate(-1)}
            className="btn btn-circle bg-white text-primary hover:bg-secondary hover:text-white shadow-xl mr-2"
          >
            ←
          </button>
        )}
        <label
          htmlFor="dashboard-drawer"
          className="
            btn btn-circle
            bg-white
            text-primary
            hover:bg-secondary
            hover:text-white
            shadow-xl
            transition-colors
            z-[9999]
          "
          aria-label="Ouvrir le menu latéral"
          aria-controls="dashboard-drawer"
          aria-expanded="false"
        >
          ☰
        </label>
      </div>
    </header>
  );
};

const DesktopNavbar = ({
  isDesktopMenuVisible,
  toggleVisibility,
  onSectionClick,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('dashboard');

  const handleMenuTabClick = (sectionId) => {
    if (location.pathname === "/dashboard") {
      // Force scroll to element with matching ID
      const element = document.getElementById(sectionId);
      if (element) {
        const yOffset = -100; // Adjust offset as needed
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
      } else {
        // Fallback to the passed onSectionClick if element not found
        onSectionClick(sectionId);
      }
    } else {
      // Navigate to dashboard with state
      navigate('/dashboard', { state: { scrollTo: sectionId } });
    }
    toggleVisibility();
  };

  // Handle click on Clico Search - Modified for direct navigation
  const handleClicoSearchClick = () => {
    navigate('/clico-search');
    toggleVisibility();
  };

  const menuTabs = useMemo(() => [
    { anchor: "guides", label: "Guides", icon: "📚" },
    { anchor: "calculators", label: "Calculatrice", icon: "🧮" },
    { anchor: "checklists", label: "Checklists", icon: "✅" },
    { anchor: "timelines", label: "Frise", icon: "⏱️" },
    { anchor: "videos", label: "Tutos", icon: "🎥" },
    { anchor: "orientation", label: "Orientation", icon: "🧭" },
    {
      anchor: "clico-search",
      label: "ClicoSearch", // Updated Label for clarity
      icon: "🔍",
      // isNew: true, // Removed isNew property
      onClick: handleClicoSearchClick
    }
  ], [i18n.language, handleClicoSearchClick, navigate, toggleVisibility]); // Added navigate and toggleVisibility to dependencies

  return (
    <>
      <nav
        className="
          navbar
          bg-primary text-white
          shadow-sm
          px-4
          fixed top-0 left-0 right-0
          z-[9999]
          h-16
          flex items-center
          mt-12
          md:mt-0
        "
        role="navigation"
      >
        <div className="flex-1 flex items-center">
          <a
            href="#welcomeSection"
            className="flex items-center"
            onClick={(e) => {
              e.preventDefault();
              onSectionClick("welcomeSection");
            }}
          >
            <img src={logo} alt="Logo" className="h-8 w-8 mr-2" />
            <span className="text-xl font-bold">
              clicdroits<span className="text-secondary">.info</span>
            </span>
          </a>
        </div>

        <div className="flex-1 flex justify-center">
          {location.pathname !== "/dashboard" && (
            <button
              onClick={() => navigate(-1)}
              className="bg-white text-primary rounded-xl px-4 py-2 shadow font-semibold hover:bg-secondary hover:text-white transition-colors mr-2"
            >
              {t('dashNav.back')}
            </button>
          )}
          <button
            type="button"
            onClick={toggleVisibility}
            className="
              bg-white
              text-primary
              rounded-xl
              px-4 py-2
              shadow
              font-semibold
              hover:bg-secondary
              hover:text-white
              transition-colors
            "
          >
            {isDesktopMenuVisible ? t('dashNav.close') : t('dashNav.myPlumMenu')}
          </button>
        </div>

        <div className="flex-1 flex items-center justify-end space-x-3">
          {/* Language Switcher */}
          <div className="mr-2">
            <LanguageSwitcher />
          </div>

          {/* Home button to return to landing page */}
          <button
            onClick={() => navigate('/')}
            className="
              bg-white
              text-primary
              rounded-xl
              font-semibold
              px-4 py-2
              shadow-md
              hover:bg-secondary
              hover:text-white
              transition-colors
            "
          >
            {t('dashNav.backToHome')}
          </button>
        </div>
      </nav>

      {isDesktopMenuVisible && (
        <div
          className="
            shadow
            fixed left-0 z-[9998] w-full
            bg-white
            border-t border-primary/30
            animate-fadeIn
          "
          style={{ top: "4rem" }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center p-4 gap-2 justify-center">
              {menuTabs.map((tab) => (
                <button
                  key={tab.anchor}
                  onClick={(e) => {
                    e.preventDefault(); 
                    tab.onClick ? tab.onClick() : handleMenuTabClick(tab.anchor);
                  }}
                  className="flex items-center px-4 py-2 mr-2 rounded-lg transition-colors shadow text-sm bg-gray-100 text-primary hover:bg-secondary hover:text-white relative"
                >
                  {typeof tab.icon === 'string' ? (
                    <span className="text-lg mr-1">{tab.icon}</span>
                  ) : (
                    <span className="w-5 h-5 mr-1 flex items-center justify-center">
                      <img src={tab.icon} alt="" className="w-5 h-5" />
                    </span>
                  )}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const DrawerMenu = ({ onSectionClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('dashboard');
  
  // Handle click on Clico Search - Modified for direct navigation
  const handleClicoSearchClick = useMemo(() => () => {
    const drawerCheckbox = document.getElementById('dashboard-drawer');
    if (drawerCheckbox) {
      drawerCheckbox.checked = false;
    }
    navigate('/clico-search');
  }, [navigate]);
  
  const menuTabs = useMemo(() => [
    { anchor: "guides", label: "Guides", icon: "📚" },
    { anchor: "calculators", label: "Calculatrice", icon: "🧮" },
    { anchor: "checklists", label: "Checklists", icon: "✅" },
    { anchor: "timelines", label: "Frise", icon: "⏱️" },
    { anchor: "videos", label: "Tutos", icon: "🎥" },
    { anchor: "orientation", label: "Orientation", icon: "🧭" },
    {
      anchor: "clico-search",
      label: "ClicoSearch", // Updated Label for clarity
      icon: "🔍",
      // isNew: true, // Removed isNew property
      onClick: handleClicoSearchClick
    }
  ], [i18n.language, handleClicoSearchClick]);

  const handleMenuTabClick = (sectionId) => {
    // First, close the drawer
    const drawerCheckbox = document.getElementById('dashboard-drawer');
    if (drawerCheckbox) {
      drawerCheckbox.checked = false;
    }

    if (location.pathname === "/dashboard") {
      // Force scroll to element with matching ID
      const element = document.getElementById(sectionId);
      if (element) {
        const yOffset = -100; // Adjust offset as needed
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        
        // Use a small delay to ensure drawer closing animation starts
        setTimeout(() => {
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }, 100);
      } else {
        // Fallback to the passed onSectionClick if element not found
        setTimeout(() => {
          onSectionClick(sectionId);
        }, 100);
      }
    } else {
      // Navigate to dashboard with state
      navigate('/dashboard', { state: { scrollTo: sectionId } });
    }
  };

  return (
    <ul className="menu p-4 w-64 min-h-full bg-white text-primary">
      <li className="mb-3">
        <div className="pt-16 flex items-center justify-center gap-2">
          <a href="/">
            <img src={logo} alt="Logo" className="h-10 w-10" />
          </a>
          <span className="text-xl font-semibold">
            clicdroits<span className="text-secondary">.info</span>
          </span>
        </div>
      </li>
      <div className="divider"></div>
      {menuTabs.map((tab) => (
        <li key={tab.anchor} className="mb-1">
          <button
            onClick={(e) => {
              e.preventDefault();
              tab.onClick ? tab.onClick() : handleMenuTabClick(tab.anchor);
            }}
            className="flex w-full text-left py-2 px-3 rounded-lg hover:bg-secondary hover:text-white transition-colors relative"
          >
            <div className="flex items-center">
              {typeof tab.icon === 'string' ? (
                <span className="text-lg mr-2">{tab.icon}</span>
              ) : (
                <span className="w-5 h-5 mr-2 flex items-center justify-center">
                  <img src={tab.icon} alt="" className="w-5 h-5" />
                </span>
              )}
              {tab.label}
            </div>
          </button>
        </li>
      ))}
      <div className="divider my-4"></div>
      {/* Home button */}
      <li>
        <Link
          to="/"
          className="flex py-2 px-3 rounded-lg hover:bg-secondary hover:text-white transition-colors"
        >
          {t('dashNav.backToHome')}
        </Link>
      </li>
    </ul>
  );
};

const DashNavbar = ({ onSectionClick }) => {
  const navigate = useNavigate();
  const [isDesktopMenuVisible, setDesktopMenuVisible] = useState(false);
  const { t } = useTranslation('dashboard');

  const toggleDesktopMenuVisibility = () =>
    setDesktopMenuVisible((prev) => !prev);

  return (
    <div className="drawer drawer-mobile w-full transition-all duration-300">
      <input
        id="dashboard-drawer"
        type="checkbox"
        className="drawer-toggle"
        aria-hidden="true"
      />
      <div className="drawer-content flex relative">
        <MobileHeader />
        <div className="hidden lg:block">
          <DesktopNavbar
            isDesktopMenuVisible={isDesktopMenuVisible}
            toggleVisibility={toggleDesktopMenuVisibility}
            onSectionClick={onSectionClick}
          />
        </div>
        <main
          className="
            p-4
            mt-0
            lg:mt-6
          "
          role="main"
        >
          {/* Content can be dynamically injected here */}
        </main>
      </div>

      <div
        className="
          drawer-side
          lg:pt-0
          z-[9999]
          mt-[64px] // Add top margin for mobile
          lg:mt-0   // Remove margin for desktop
        "
      >
        <label
          htmlFor="dashboard-drawer"
          className="drawer-overlay"
          aria-hidden="true"
        />
        <DrawerMenu onSectionClick={onSectionClick} />
      </div>
    </div>
  );
};

export default DashNavbar;
